import { ChevronRight, Gem, X } from "lucide-react";
import { useEffect, useState } from "react";

import { FeedbackButton } from "@/components/feedback/FeedbackButton";
import { AccountMenu } from "@/components/shell/AccountMenu";
import { PersonaSwitcher } from "@/components/shell/PersonaSwitcher";
import { SectionNavCount } from "@/components/shell/SectionNavCount";
import { ThemeToggle } from "@/components/shell/ThemeToggle";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  useSidebar,
} from "@/components/ui/sidebar";
import type { ModuleDefinition, SectionDefinition } from "@/config/modules";
import { LIVE } from "@/lib/live";

/*
  The persona switcher is a fixtures-only demo control (#189): with a real
  signed-in identity behind `AccountMenu`, swapping personas out from under it
  would just be confusing. `LIVE` is the same switch as `auth/backend.ts` and
  `AppPermissions` use.
*/

/**
 * Which modules the user last left expanded, keyed by module id.
 *
 * A cookie carries the sidebar's own expanded/collapsed state (see
 * `sidebar.tsx`) because that one has to be read before first paint. This one
 * doesn't — it only matters once a module row exists to open — so
 * `localStorage` is the plainer fit.
 */
const OPEN_MODULES_STORAGE_KEY = "estelle-sidebar-open-modules";

function readStoredOpenModules(): Set<string> {
  try {
    const raw = localStorage.getItem(OPEN_MODULES_STORAGE_KEY);
    if (!raw) return new Set();
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((id): id is string => typeof id === "string"));
  } catch {
    // A private window or blocked storage just means this resets next visit.
    return new Set();
  }
}

interface AppSidebarProps {
  /**
   * Which modules to offer, and which of each module's sections. Narrowed by
   * permission before it gets here — the sidebar renders what it is given.
   */
  modules: { module: ModuleDefinition; sections: SectionDefinition[] }[];
  activeModuleId: string;
  activeSectionId: string;
  /**
   * Called when an entry is chosen. The sidebar does not navigate itself: a
   * presentational sidebar is what lets the shell own the route, the same
   * reason the rail it replaces did not either.
   */
  onSelect: (module: ModuleDefinition, section: SectionDefinition) => void;
}

/**
 * Modules, each with its sections collapsible under it.
 *
 * This replaced the 80px icon rail plus the section tabs in the page header
 * (#346). Two levels of navigation in one column, which is what a phone needs
 * — the rail alone took a fifth of a 390px screen and the tabs overflowed the
 * header — and it is also one place to change section rather than two that can
 * disagree about which one is open.
 *
 * #360 made the section lists collapsible rather than tied to which module is
 * active: several can be open at once, and which ones is remembered, because
 * "only the active module's sections exist" turned out to just mean re-opening
 * the same module over and over to see where else to go.
 */
export function AppSidebar({
  modules,
  activeModuleId,
  activeSectionId,
  onSelect,
}: AppSidebarProps) {
  const { isMobile, setOpenMobile, state } = useSidebar();
  // Icon mode only exists on the desktop column — the mobile sheet is always
  // full width, so a click there is never ambiguous between "open" and "go".
  const isIconMode = state === "collapsed" && !isMobile;

  const [openModules, setOpenModules] = useState<Set<string>>(() => {
    const stored = readStoredOpenModules();
    stored.add(activeModuleId);
    return stored;
  });

  // The active module starts open — including when it becomes active by
  // navigating in from outside the sidebar, not just on first mount. Adjusted
  // during render rather than in an effect (the state-from-props pattern React
  // recommends over a setState-in-effect, which would cost an extra paint):
  // tracking the previous id is what makes this "only when it changes" rather
  // than every render, so a module the user has since closed by hand stays
  // closed instead of snapping back open.
  const [prevActiveModuleId, setPrevActiveModuleId] = useState(activeModuleId);
  if (activeModuleId !== prevActiveModuleId) {
    setPrevActiveModuleId(activeModuleId);
    setOpenModules((prev) =>
      prev.has(activeModuleId) ? prev : new Set(prev).add(activeModuleId),
    );
  }

  useEffect(() => {
    try {
      localStorage.setItem(
        OPEN_MODULES_STORAGE_KEY,
        JSON.stringify([...openModules]),
      );
    } catch {
      // Best-effort persistence; nothing to fall back to here.
    }
  }, [openModules]);

  function setModuleOpen(moduleId: string, open: boolean) {
    setOpenModules((prev) => {
      const next = new Set(prev);
      if (open) next.add(moduleId);
      else next.delete(moduleId);
      return next;
    });
  }

  function select(module: ModuleDefinition, section: SectionDefinition) {
    onSelect(module, section);
    // The sheet covers the page it has just navigated, so on a phone choosing
    // a destination has to also get out of the way of it.
    if (isMobile) setOpenMobile(false);
  }

  return (
    <Sidebar>
      <SidebarHeader>
        <div className="flex items-center gap-2 px-1 py-1">
          <Gem className="text-primary size-5 shrink-0" aria-hidden />
          <span className="truncate font-semibold tracking-tight group-data-[collapsible=icon]:hidden">
            EstelleERP
          </span>
          {isMobile ? (
            <div className="ml-auto flex items-center gap-1">
              {/*
                The navigation sheet is modal, so the page header's Feedback
                button is under its overlay while it is open (#511). Not shown
                on the desktop column: that one is not modal, and the header's
                button is right there.
              */}
              <FeedbackButton />
              <Button
                variant="ghost"
                size="icon"
                className="size-8"
                onClick={() => {
                  setOpenMobile(false);
                }}
              >
                <X />
                <span className="sr-only">Close navigation</span>
              </Button>
            </div>
          ) : null}
        </div>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarMenu>
            {modules.map(({ module, sections }) => {
              const Icon = module.icon;
              const isActiveModule = module.id === activeModuleId;
              const first = sections[0];

              // A module with one section has nothing to expand — Dashboard
              // is just a link, the way every module used to be.
              if (sections.length <= 1) {
                return (
                  <SidebarMenuItem key={module.id}>
                    <SidebarMenuButton
                      isActive={isActiveModule}
                      tooltip={module.label}
                      aria-current={isActiveModule ? "page" : undefined}
                      onClick={() => {
                        if (first) select(module, first);
                      }}
                    >
                      <Icon aria-hidden />
                      <span className="truncate">{module.label}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              }

              const isModuleOpen = openModules.has(module.id);

              return (
                <Collapsible
                  key={module.id}
                  asChild
                  open={isModuleOpen}
                  onOpenChange={(open) => {
                    setModuleOpen(module.id, open);
                  }}
                  className="group/collapsible"
                >
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      isActive={isActiveModule}
                      tooltip={module.label}
                      aria-current={isActiveModule ? "page" : undefined}
                      aria-expanded={isModuleOpen}
                      // With its sections listed under it, the open one is
                      // what carries the fill — two filled rows stacked read
                      // as two places at once. Closed, or collapsed to icons,
                      // the module row is the only marker left and keeps it.
                      className={
                        isModuleOpen && !isIconMode
                          ? "data-[active=true]:bg-transparent data-[active=true]:hover:bg-sidebar-accent"
                          : undefined
                      }
                      onClick={() => {
                        // Collapsed to icons, there is no sub-list to open —
                        // a click is the same shortcut to the first section it
                        // always was. Expanded, the row's job changes to
                        // toggling the list under it.
                        if (isIconMode) {
                          if (first) select(module, first);
                          return;
                        }
                        setModuleOpen(module.id, !isModuleOpen);
                      }}
                    >
                      <Icon aria-hidden />
                      <span className="truncate">{module.label}</span>
                      <ChevronRight
                        aria-hidden
                        className={
                          "ml-auto transition-transform " +
                          "group-data-[state=open]/collapsible:rotate-90 " +
                          "group-data-[collapsible=icon]:hidden"
                        }
                      />
                    </SidebarMenuButton>

                    <CollapsibleContent>
                      <SidebarMenuSub>
                        {sections.map((section) => {
                          // Section ids repeat across modules ("all"), so the
                          // module has to match too (#569).
                          const isActiveSection =
                            isActiveModule && section.id === activeSectionId;
                          return (
                            <SidebarMenuSubItem key={section.id}>
                              <SidebarMenuSubButton
                                asChild
                                isActive={isActiveSection}
                              >
                                <button
                                  type="button"
                                  aria-current={
                                    isActiveSection ? "page" : undefined
                                  }
                                  onClick={() => {
                                    select(module, section);
                                  }}
                                >
                                  <span className="truncate">
                                    {section.label}
                                  </span>
                                  <SectionNavCount
                                    moduleId={module.id}
                                    sectionId={section.id}
                                  />
                                </button>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                          );
                        })}
                      </SidebarMenuSub>
                    </CollapsibleContent>
                  </SidebarMenuItem>
                </Collapsible>
              );
            })}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>

      {/* Settings, not destinations — which is why they sit apart from the
          modules above rather than at the end of them. */}
      <SidebarFooter className="border-t">
        <SidebarMenu>
          {LIVE ? null : <PersonaSwitcher />}
          <AccountMenu />
          <ThemeToggle />
        </SidebarMenu>
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  );
}
